import type { Role } from './roles';
import type { Permission } from './permissions';

/**
 * Concrete Role → Permission mapping per docs/architecture/02-security-architecture.md.
 * This is the ONLY place a role is translated into permissions; application code
 * must always go through `can()` with the resulting `AuthorizedUser.permissions`,
 * never compare `role` directly (see packages/auth/src/can.ts).
 *
 * Only the three ACTIVE_ROLES are given real permissions in this phase — the
 * remaining roles (investor, customer, guest, moderator) stay reserved (empty)
 * until their features are implemented, per the approved RBAC model.
 */
const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  super_admin: [
    'project:create',
    'project:update',
    'project:delete',
    'project:publish',
    'news:create',
    'news:update',
    'news:delete',
    'news:publish',
    'media:upload',
    'media:delete',
    'settings:update',
    'user:manage',
    'role:manage',
    'scientific-entry:manage',
    'admin:access'
  ],
  editor: [
    'project:create',
    'project:update',
    'project:publish',
    'news:create',
    'news:update',
    'news:publish',
    'media:upload',
    'scientific-entry:manage',
    'admin:access'
  ],
  viewer: ['admin:access'],
  investor: [],
  customer: [],
  guest: [],
  moderator: []
};

/**
 * Looks up the permission set for a role. Returns an empty, readonly array for
 * any role with no mapping (defensive default — never throws, never grants
 * access by omission).
 */
export function permissionsForRole(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}
